import React, { createContext, useContext, useMemo } from "react"
import { Spin, Result, Button, Layout } from "antd"
import { useConfig } from "@mimber/client/mimber-organization"
import type {
  FeatureToggleContextValue,
  FeatureToggleProviderProps,
} from "./FeatureToggleProvider.types"

const { Content } = Layout

const FeatureToggleContext = createContext<FeatureToggleContextValue | null>(
  null
)

/**
 * FeatureToggleProvider
 * Loads feature flags from the config endpoint and provides them throughout the app
 * Shows loading state while config is being fetched
 * Shows error state if config fetch fails
 */
export const FeatureToggleProvider: React.FunctionComponent<
  FeatureToggleProviderProps
> = ({ children }) => {
  const { data: configResponse, isLoading, error, refetch } = useConfig()

  const featureToggles = useMemo(() => {
    return configResponse?.data?.featureFlags || []
  }, [configResponse])

  const isFeatureEnabled = useMemo(() => {
    return (key: string): boolean => {
      const flag = featureToggles.find((f) => f.key === key)
      // If flag doesn't exist, default to disabled
      // If flag exists, use its enabled value
      return flag ? flag.enabled : false
    }
  }, [featureToggles])

  const value: FeatureToggleContextValue = useMemo(
    () => ({
      featureToggles,
      isFeatureEnabled,
    }),
    [featureToggles, isFeatureEnabled]
  )

  // Loading state - show spinner
  if (isLoading) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Content
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          <Spin size="large" />
          <p>Loading features...</p>
        </Content>
      </Layout>
    )
  }

  // Error state - show error with retry button
  if (error) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Content>
          <Result
            status="error"
            title="Failed to Load Features"
            subTitle="Unable to load feature configuration. Please try again."
            extra={[
              <Button type="primary" key="retry" onClick={() => refetch()}>
                Retry
              </Button>,
            ]}
          />
        </Content>
      </Layout>
    )
  }

  return (
    <FeatureToggleContext.Provider value={value}>
      {children}
    </FeatureToggleContext.Provider>
  )
}

/**
 * Hook to check if a feature is enabled
 * Returns true if feature flag exists and is enabled, or if flag doesn't exist (default enabled)
 * Returns false only if feature flag exists and is disabled
 */
export const useFeatureToggle = (key: string): boolean => {
  const context = useContext(FeatureToggleContext)
  if (!context) {
    throw new Error(
      "useFeatureToggle must be used within a FeatureToggleProvider"
    )
  }
  return context.isFeatureEnabled(key)
}

/**
 * Hook to get all feature toggles
 */
export const useFeatureToggles = () => {
  const context = useContext(FeatureToggleContext)
  if (!context) {
    throw new Error(
      "useFeatureToggles must be used within a FeatureToggleProvider"
    )
  }
  return context.featureToggles
}
